// src/components/ui/dialog.tsx
"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "lib/utils";

/** Re-exports */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

/** Overlay */
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[100] bg-[color:var(--overlay)] backdrop-blur-[3px] animate-overlay-in",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

/** Sizing / Alignment */
type Size = "sm" | "md" | "lg" | "xl" | "full";
type Align = "center" | "top";

const SIZE: Record<Size, string> = {
  sm: "w-[min(420px,92vw)]",
  md: "w-[min(560px,94vw)]",
  lg: "w-[min(720px,96vw)]",
  xl: "w-[min(960px,98vw)]",
  full: "w-[min(1200px,98vw)] h-[min(90vh,90dvh)]",
};

const ALIGN: Record<Align, string> = {
  center: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  top: "top-[6vh] left-1/2 -translate-x-1/2",
};

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: Size;
  align?: Align;
  /** Adds padding to the animated inner wrapper */
  padding?: boolean;
  /** Renders the "×" close button in the top-right */
  showClose?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className = "",
      children,
      size = "md",
      align = "center",
      padding = true,
      showClose = true,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-align={align}
        // prevent auto-focusing the first button (keeps heavy blue ring off)
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          // Surface + dark theme
          "fixed z-[110]",
          // Border + shadow tuned for dark
          "rounded-2xl border border-line",
          "shadow-overlay",
          // Position & size
          ALIGN[align],
          SIZE[size],
          // Height cap
          "max-h-[calc(100dvh-12vh)] md:max-h-[calc(100dvh-12vh)]",
          // Flex column layout
          "flex flex-col",
          className
        )}
        {...props}
      >
        <div className={cn("modal-content flex flex-col flex-1 overflow-hidden bg-surface text-content rounded-2xl", padding && "p-5")}>
          {children}
        </div>

        {showClose && (
          <DialogPrimitive.Close
            className={cn(
              "absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-xl",
              "text-content-muted hover:text-content hover:bg-surface-hover",
              "focus-ring"
            )}
            aria-label="Close"
          >
            <span className="text-xl leading-none">×</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = "DialogContent";

/** Sections */
export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("", className)} {...props} />
);

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "pt-5 flex items-center justify-end gap-2",
      className
    )}
    {...props}
  />
);

/** Body: scrollable area */
export const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex-1 overflow-y-auto px-5 pb-5 pt-2", className)}
    {...props}
  />
);

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("mt-1 text-ui text-content-muted", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
